import { invalidateAllTagFilterQueries, invalidateAllTagSearchQueries } from '@Queries/index';
import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import { FC, useCallback, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Waypoint } from 'react-waypoint';
import { UseSelectionStateType } from '../../../../../hooks/useSelectionState';
import { ILocationState } from '../../../../../models';
import { useUserSession } from '../../../../../models/hooks/appStateHooks';
import { Checkbox } from '../../../../components-v2/Checkbox';
import { ButtonBase } from '../../../../components/Button';
import { Databoard, DataboardBody } from '../../../../components/Databoard';
import {
	DataGrid,
	DataGridColumn,
	DataGridColumns,
	DataGridRow,
	DataGridRowItem,
	DataGridRows,
} from '../../../../components/DataGrid';
import { EntityTablePlaceholder } from '../../../../components/entities/EntityTablePlaceholder';
import { LoadingSpinner } from '../../../../components/LoadingSpinner';
import { Modal } from '../../../../components/Modal';
import { EditTagAlert } from '../../../../components/reporting/tags/EditTagAlert';
import { DisclosureIcon } from '../../../../components/svgs/icons/DisclosureIcon';
import { useTagsDataContext } from '../TagsContexts/TagsDataContext';
import { useTagsSearchContext } from '../TagsContexts/TagsSearchContext';
import { DefaultTagsFilter, DefaultTagsSort } from '../TagsUtilities';
import { styleSheet } from './styles';
import { TagCategoryRow } from './TagCategoryRow';
import { TagRow } from './TagRow';

interface TagsTableProps {
	clearSearch: () => void;
	isAdminContext: boolean;
	impersonationContext?: Api.IImpersonationContext;
	selectionState: UseSelectionStateType;
	openUpdateCategoryModal: () => void;
}

export const TagsTable: FC<TagsTableProps> = ({
	clearSearch,
	isAdminContext = false,
	impersonationContext,
	selectionState,
	openUpdateCategoryModal,
}) => {
	const userSession = useUserSession();
	const location = useLocation<ILocationState<any, Api.ITagManagementCommand>>();

	const { tags, categories, selectedCategory, setSelectedTag, isFetching } = useTagsDataContext();
	const { sort, setSort, setFilter } = useTagsSearchContext();
	const { toggleRow, isSelected, isAllSelected, isPartialSelected, toggleSelectAll, reset } = selectionState;

	const [editTagAlertToEditVm, setEditTagAlertToEditVm] = useState<Api.TagViewModel>(null);

	const onClearButtonClicked = () => {
		setFilter(DefaultTagsFilter);
		setSort(DefaultTagsSort);
		clearSearch();
		reset();
	};

	const onEditTagAlert = (tag: Api.IAccountTag) => {
		setEditTagAlertToEditVm(new Api.TagViewModel(userSession, tag));
	};

	const refreshTags = useCallback(() => {
		invalidateAllTagFilterQueries();
		invalidateAllTagSearchQueries();
	}, []);

	const onEditTagAlertModalClose = useCallback(() => {
		setEditTagAlertToEditVm(null);
		refreshTags();
	}, [refreshTags]);

	const queryCommand = location.state?.model;
	if (
		queryCommand?.command === 'tag-management' &&
		queryCommand.op === Api.TagManagementCommandOperation.SetTagNotification
	) {
		const tagManagementCommand = queryCommand as Api.ITagManagementCommand;
		if (tagManagementCommand.tag) {
			onEditTagAlert(tags.data.find(tag => tag.name === tagManagementCommand.tag));
			location.state.model = null;
		}
	}

	const handleUpdateCategory = (tag: Api.IAccountTag) => {
		openUpdateCategoryModal();
		setSelectedTag(tag);
	};

	const handleLoadMore = useCallback(() => {
		if (selectedCategory) return;
		if (categories.hasNextPage) {
			categories.fetchNextPage();
		} else {
			tags.fetchNextPage();
		}
	}, [categories, selectedCategory, tags]);

	const sortDirectionDesc = sort.sort === 'desc';

	const toggleContactsSort = () => {
		setSort({
			sort: sort.sort === 'asc' ? 'desc' : 'asc',
			sortBy: 'stats.contacts',
		});
	};
	const toggleCompaniesSort = () => {
		setSort({
			sort: sort.sort === 'asc' ? 'desc' : 'asc',
			sortBy: 'stats.companies',
		});
	};
	const toggleTagNotificationsSort = () => {
		setSort({ sort: sort.sort === 'asc' ? 'desc' : 'asc', sortBy: 'tagAlert' });
	};
	const toggleTagSort = () => {
		setSort({ sort: sort.sort === 'asc' ? 'desc' : 'asc', sortBy: 'tag' });
	};

	const shouldDisplayWaypoint =
		!selectedCategory &&
		!(tags.isFetchingNextPage || categories.isFetchingNextPage) &&
		(categories.hasNextPage || tags.hasNextPage);

	return (
		<Databoard styles={[styleSheet.databoard]}>
			<DataboardBody data-scroll-restoration-id='tags-table' styles={[styleSheet.databoardBody]}>
				<DataGrid>
					<DataGridColumns>
						<DataGridColumn sticky>
							<Checkbox
								id='select-all-tags'
								checked={isPartialSelected ? 'indeterminate' : isAllSelected}
								onCheckedChange={toggleSelectAll}
								size='lg'
							/>
						</DataGridColumn>
						<DataGridColumn sticky /> {/* Favorite */}
						<DataGridColumn sticky /> {/* ColorPicker */}
						<DataGridColumn sticky /> {/* Lock toggle */}
						<DataGridColumn sticky styles={[styleSheet.nameColumn]}>
							<SortToggle
								sortDirectionDesc={sortDirectionDesc}
								label='Tag'
								onClick={toggleTagSort}
								sortBy={sort.sortBy}
								sortName='tag'
							/>
						</DataGridColumn>
						<DataGridColumn sticky styles={[styleSheet.contactsColumn]}>
							<SortToggle
								sortDirectionDesc={sortDirectionDesc}
								label='Contacts'
								onClick={toggleContactsSort}
								sortBy={sort.sortBy}
								sortName='stats.contacts'
							/>
						</DataGridColumn>
						<DataGridColumn sticky styles={[styleSheet.companiesColumn]}>
							<SortToggle
								sortDirectionDesc={sortDirectionDesc}
								label='Companies'
								onClick={toggleCompaniesSort}
								sortBy={sort.sortBy}
								sortName='stats.companies'
							/>
						</DataGridColumn>
						<DataGridColumn sticky styles={[styleSheet.tagAlertColumn]}>
							<SortToggle
								sortDirectionDesc={sortDirectionDesc}
								label='Tag Notifications'
								onClick={toggleTagNotificationsSort}
								sortBy={sort.sortBy}
								sortName='tagAlert'
							/>
						</DataGridColumn>
						<DataGridColumn sticky /> {/** Ellipsis menu */}
					</DataGridColumns>

					<DataGridRows>
						{/**
						 * Always display categories first
						 */}
						{!selectedCategory &&
							!!categories?.data?.length &&
							categories.data.map(category => (
								<TagCategoryRow
									key={category.id}
									category={category}
									impersonationContext={impersonationContext}
									reset={reset}
								/>
							))}
						{/**
						 * Do not display tags until all categories have been displayed
						 */}
						{(!categories.hasNextPage || !!selectedCategory) &&
							!!tags?.data?.length &&
							tags.data.map(tag => (
								<TagRow
									tag={tag}
									key={tag.id}
									isSelected={isSelected(tag.id)}
									impersonationContext={impersonationContext}
									isAdminContext={isAdminContext}
									onToggleTagRow={toggleRow}
									onEditTagAlert={onEditTagAlert}
									refreshTags={refreshTags}
									onUpdateCategory={handleUpdateCategory}
								/>
							))}
						{!isFetching && !tags?.data?.length && !categories?.data?.length && (
							<DataGridRow>
								<DataGridRowItem fullColSpan>
									<EntityTablePlaceholder onClearButtonClicked={onClearButtonClicked} type='tag' />
								</DataGridRowItem>
							</DataGridRow>
						)}
					</DataGridRows>
				</DataGrid>

				{isFetching && <LoadingSpinner type='large' />}

				{shouldDisplayWaypoint && <Waypoint bottomOffset='-200px' onEnter={handleLoadMore} />}

				{editTagAlertToEditVm && <EditTagAlertModal tagVm={editTagAlertToEditVm} onClose={onEditTagAlertModalClose} />}
			</DataboardBody>
		</Databoard>
	);
};

const SortToggle = ({
	label,
	sortDirectionDesc,
	onClick,
	sortBy,
	sortName,
}: {
	label: string;
	sortDirectionDesc: boolean;
	onClick: () => void;
	sortBy: string;
	sortName: string;
}) => {
	const isSortingThisColumn = sortBy === sortName;
	const [isHovering, setIsHovering] = useState(false);
	return (
		<ButtonBase
			className={css(styleSheet.sortTrigger)}
			onClick={onClick}
			onMouseEnter={() => setIsHovering(true)}
			onMouseLeave={() => setIsHovering(false)}
		>
			{label}
			{(isSortingThisColumn || isHovering) && (
				<span className={css(styleSheet.sortIcon, sortDirectionDesc && styleSheet.sortIconFlipped)}>
					<DisclosureIcon type='chevron' />
				</span>
			)}
		</ButtonBase>
	);
};

const EditTagAlertModal = ({ tagVm, onClose }: { tagVm: Api.TagViewModel; onClose: () => void }) => {
	return (
		<Modal isOpen={!!tagVm} onClose={onClose}>
			<EditTagAlert tagVm={tagVm} onClose={onClose} />
		</Modal>
	);
};
